import { Box, Tab, Tabs } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ClassifierMainData from "../components/classifier/ClassifierMainData";
import ClassifierPassportData from "../components/classifier/ClassifierPassportData";
import NotFoundBlock from "../components/NotFoundBlock";
import { ApiRoutes } from "../configuration/apiRoutes";
import { defaultLocale as defaultLocaleCode } from "../constants/languages";
import {
    resetBreadcrumbState,
    setBreadcrumbClassifierCode,
} from "../features/breadcrumbs";
import { useAppDispatch } from "../hooks/redux";
import { Translations } from "../localization/translations";
import { ClassifierDto } from "../models/ClassifierDto";
import PageContent from "../parts/page-content/PageContent";
import { RestService } from "../utils/axios";
import useKlasisTracker from "../utils/klasisTracker";

interface IClassifierTabPanel {
    title: string;
    index: number;
    contentElement: JSX.Element;
}

const Classifier: React.FC = () => {
    let { id, locale } = useParams();

    const { klasisTrackPageView } = useKlasisTracker();
    const { t } = useTranslation();

    const [data, setData] = useState<ClassifierDto>();
    const [activeTabIndex, setActiveTabIndex] = useState<number>(1);
    const [isNotFound, setIsNotFound] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const appDispatch = useAppDispatch();

    const tabs: IClassifierTabPanel[] = [
        {
            title: t(Translations.classifier.tabItems.classifierAndDownloads),
            index: 1,
            contentElement: (
                <ClassifierMainData
                    data={data}
                    setPassportDataTab={() => setActiveTabIndex(2)}
                />
            ),
        },
        {
            title: t(Translations.classifier.tabItems.passportData),
            index: 2,
            contentElement: <ClassifierPassportData data={data} />,
        },
    ];

    const refreshData = () => {
        if (!id) {
            setIsNotFound(true);
            return;
        }

        setIsLoading(true);

        RestService.Get<ClassifierDto>(
            ApiRoutes.Classifiers.Get({
                id: id ?? "",
                localeCode: locale ?? defaultLocaleCode,
            }),
            (response: ClassifierDto) => {
                setIsNotFound(false);

                setData(response);

                appDispatch(setBreadcrumbClassifierCode(response.abbreviation));
            },
            (error: AxiosError) => {
                if (error.response?.status === 404) setIsNotFound(true);
                console.log("Error: " + error.message);
            },
            () => {
                setIsLoading(false);
            }
        );
    };

    useEffect(() => {
        klasisTrackPageView();

        return () => {
            appDispatch(resetBreadcrumbState());
        };
    }, []);

    useEffect(() => {
        refreshData();
    }, [locale]);

    const handleTabChange = (
        event: React.SyntheticEvent,
        newTabIndex: number
    ) => {
        setActiveTabIndex(newTabIndex);
    };

    if (isNotFound) return <NotFoundBlock />;

    return (
        <PageContent
            title={
                (data?.abbreviation ? `${data?.abbreviation}: ` : "") +
                data?.title
            }
            showBreadcrumbs={true}
            showTitleBarCommands={true}
            isLoading={isLoading}
        >
            <Box>
                <Tabs
                    variant="scrollable"
                    allowScrollButtonsMobile={true}
                    value={activeTabIndex}
                    onChange={handleTabChange}
                    role="tablist"
                    aria-orientation="horizontal"
                    aria-labelledby={`tab-${activeTabIndex}`}
                >
                    {tabs.map((tab) => (
                        <Tab
                            value={tab.index}
                            label={tab.title}
                            tabIndex={0}
                            key={`tab-${tab.index}`}
                            role="tab"
                            id={`tab-${tab.index}`}
                            aria-controls={`tabpanel-${tab.index}`}
                        />
                    ))}
                </Tabs>

                {tabs.map((tab) => (
                    <ClassifierTabPanel
                        currentTabIndex={activeTabIndex}
                        correspondingTabIndex={tab.index}
                        key={`tab-content-${tab.index}`}
                    >
                        {tab.contentElement}
                    </ClassifierTabPanel>
                ))}
            </Box>
        </PageContent>
    );
};

interface IClassifierTabPanelProps {
    correspondingTabIndex: number;
    currentTabIndex: number;
    children?: JSX.Element | JSX.Element[];
}

const ClassifierTabPanel = (props: IClassifierTabPanelProps): JSX.Element => {
    return (
        <Box
            marginTop={2}
            hidden={props.currentTabIndex !== props.correspondingTabIndex}
            role="tabpanel"
            id={`tabpanel-${props.correspondingTabIndex}`}
            aria-labelledby={`tab-${props.correspondingTabIndex}`}
        >
            {props.children}
        </Box>
    );
};

export default Classifier;
